import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { q } from "./api.ts";
import { PROVENANCE_LABELS, reasonText } from "./labels.ts";

// Shared by the profile and its sources: refreshing what a product's change
// touches, and where a fact or decision comes from. The first
// implementation's rows and dialogs went with its pages (plan M7 step 7j).

/** Everything that shows a product's facts, actions or opportunities is refreshed together. */
export function useRefresh(productId: string) {
  const client = useQueryClient();
  return () =>
    Promise.all(
      [["product", productId], ["product-rules", productId], ["source-review", productId], ["playbook-library", productId], ["playbook-page"], ["goals", productId], ["actions", productId], ["opportunities", productId], ["connections", productId], ["week", productId], ["products"], ["action"], ["inbox"]].map((queryKey) =>
        client.invalidateQueries({ queryKey }),
      ),
    );
}

/** Where a referenced record can be opened: the source it was read from, or its action. */
function refTarget(ref: C.ProvenanceView["used"][number]): { href: string } | { actionId: string } | null {
  const p = ref.about?.params ?? {};
  if (typeof p.url === "string" && /^https?:\/\//.test(p.url)) return { href: p.url };
  if (typeof p.action_id === "string") return { actionId: p.action_id };
  return null;
}

/**
 * Where a fact, decision, receipt, reading or lesson comes from
 * (docs/design-system.md §5.13): one grey line for the activity and on whose
 * behalf, and the references it used behind a disclosure. Nothing unrecorded
 * is guessed; it is an explanation, so it carries no colour.
 */
export function Provenance({ provenance: p, productId }: { provenance: C.ProvenanceView; productId: string }) {
  const me = useQuery({ ...q.me, retry: false });
  const L = PROVENANCE_LABELS;
  const line = L.line(p, me.data?.user.id ?? null);
  if (!p.used.length) return <p className="z-small z-muted">{line}{p.legacy ? `. ${L.legacy}` : ""}</p>;
  return (
    <details className="z-disclosure z-small z-muted">
      <summary>
        <span style={{ flex: "1 1 0", minWidth: "12em" }}>{line} · {L.references(p.used.length)}</span>
      </summary>
      <ul className="z-points">
        {p.used.map((ref) => {
          const text = ref.about ? reasonText(ref.about) : L.notFound;
          const target = refTarget(ref);
          return (
            <li key={`${ref.kind}:${ref.id}:${ref.revision ?? ""}:${ref.digest ?? ""}`}>
              {target && "href" in target ? (
                <a className="z-link" href={target.href} target="_blank" rel="noreferrer noopener" style={{ overflowWrap: "anywhere" }}>{text}</a>
              ) : target ? (
                <Link className="z-link" to="/p/$productId/actions/$actionId" params={{ productId, actionId: target.actionId }}>{text}</Link>
              ) : text}
            </li>
          );
        })}
      </ul>
      {p.legacy && <p>{L.legacy}</p>}
    </details>
  );
}
